import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { styled } from 'linaria/react';
import React from 'react';
import { FindifyTracker } from '../Findify/Findify';
import FindifyRecommendations from '../Findify/FindifyRecommendations';
import { theme } from '../Theme';
import ImageContainer from './Images/ImageContainer';
import { ProductPageInfoCard } from './ProductPageInfoCard';
import ProductPageLoadingState from './ProductPageLoadingState';
import { Container } from './styledComponents';
import ImageGrid from './Images/ImageGrid';
import { Above } from '@jetshop/ui/Breakpoints';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import { FlowboxComponent } from '../Flowbox/FlowboxPage';
import { PDPShopTheLook } from './PDPShopTheLook';
import { PDPJournalPost } from './PDPJournalPost';

const ProductContainer = styled('div')`
  /* margin-top: 40px; */
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  ${theme.below.lg} {
    margin-top: 0;
  }

  .old-price {
    margin-right: 0 !important;
  }
`;

const ProductPageSection = styled('section')`
  background: #f3f3f3;
  &.info-card {
    flex: 1;
    height: 100%;
    top: 140px;
    position: sticky !important;
    max-width: 550px;
    margin-top: 20px;
    margin-bottom: -30px;
  }
  &.image-card {
    flex: 2;
  }

  ${theme.below.lg} {
    width: 100%;
    &.info-card {
      width: 100%;
      padding: 0;
      margin-top: 0;
      margin-bottom: 0;
    }
  }
`;

const ProductPageUpper = styled('div')`
  width: 100%;
  background: #f3f3f3;
  ${theme.above.lg} {
    margin-bottom: 100px;
  }
`;

const FindifyWrapper = styled('div')`
  margin: 40px 0;
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-bottom: 6px;
  li,
  li a {
    text-decoration: none;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.03em;
    margin-bottom: 0 !important;
    color: ${theme.colors.breadcrumbgrey}!important;
  }

  li:last-of-type {
    ::after {
      content: '';
    }
  }

  ${theme.below.lg} {
    position: relative;
    margin: 1rem;
  }
`;

const ShopTheLookWrapper = styled('div')`
  width: 100%;
`;

const JournalPostsWrapper = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: row;
  max-width: 80rem;
  margin: auto;
  gap: 20px;

  ${theme.below.xl} {
    flex-direction: column;
  }

  > div {
    width: ${props => (props.columns === 1 ? '100%' : '50%')};
    ${theme.below.xl} {
      width: 100%;
    }
  }
`;

const Product = ({ result: { data, loading }, product }) => {
  const initialVariant = useVariantFromUrl();

  const variantHandler = useProductVariants(product, {
    initialVariant,
    preselectCheapest: false
  });

  const {
    selectedVariant: selectedVariation,
    getMissingOptions
  } = variantHandler;

  // const brandId = validateNumber(
  //   product?.customFields?.find(({ key }) => key === 'brand_id')?.stringValue
  // );
  // const specifications = product?.customFields;

  // Return early if there is no product
  if (!product)
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );

  const images = product.images ?? [];
  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);
  // const relatedProducts = product.relatedProducts;

  const cardProps = {
    product: product,
    variant: selectedVariation,
    variantHandler: variantHandler,
    selectedVariation: selectedVariation,
    getMissingOptions: getMissingOptions,
    parents: parents
  };

  const shopTheLookImage = product.customFields.find(
    field => field.key === 'STL_image'
  )?.stringValue;
  const shopTheLookProducts = product.customFields.find(
    field => field.key === 'STL_products'
  )?.listValues;

  const categoryPosts =
    product.categories?.reduce((posts, category) => {
      const categoryPosts =
        category?.data?.items?.filter(item => item.type === 'categoryPost') ||
        [];
      return [...posts, ...categoryPosts];
    }, []) || [];

  const postIds = categoryPosts
    .map(post => post.properties?.[0]?.value?.value)
    .filter(Boolean);

  return (
    <ProductContainer>
      <FindifyTracker
        isProdPage={true}
        product={product}
        selectedVariation={selectedVariation}
      />
      <ProductPageUpper>
        <Container className={'wide-PDP'}>
          <ProductPageSection className={'image-card'}>
            <StyledBreadcrumbs
              parents={parents}
              breadcrumbText={product?.breadcrumbText}
            />
            {images && (
              <Above breakpoint="lg">
                {matches =>
                  matches ? (
                    <ImageGrid
                      images={images}
                      product={product}
                      variant={selectedVariation}
                    />
                  ) : (
                    <ImageContainer
                      images={images}
                      variant={selectedVariation}
                      badges={product.badges}
                    />
                  )
                }
              </Above>
            )}
          </ProductPageSection>
          <ProductPageSection className={'info-card'}>
            <ProductPageInfoCard {...cardProps} />
          </ProductPageSection>
        </Container>
      </ProductPageUpper>
      {shopTheLookProducts && (
        <ShopTheLookWrapper>
          <PDPShopTheLook
            image={shopTheLookImage}
            products={shopTheLookProducts}
          />
        </ShopTheLookWrapper>
      )}

      <FindifyWrapper>
        <FindifyRecommendations
          id={'product-findify-rec-5'}
          product={product}
        />
      </FindifyWrapper>
      <JournalPostsWrapper columns={postIds.length}>
        {postIds.map((postId, index) => (
          <PDPJournalPost
            key={`journal-post-${postId}-${index}`}
            postId={postId}
            direction={postIds?.length === 1 ? 'row' : 'column'}
          />
        ))}
      </JournalPostsWrapper>
      <FindifyWrapper>
        <FindifyRecommendations
          id={'product-findify-rec-6'}
          product={product}
        />
        <FindifyRecommendations
          id={'product-findify-rec-7'}
          product={product}
        />
        <FindifyRecommendations
          id={'product-findify-rec-11'}
          product={product}
        />
      </FindifyWrapper>
      <FlowboxComponent
        tags={[]}
        flowboxKey={{ value: 'yojH_qPwRPG4zjHPItT3qQ' }}
        product={product?.articleNumber}
      />
    </ProductContainer>
  );
};

export default Product;
